import React from 'react';
import { ArrowRight, Boxes, Weight, ShieldCheck, BarChart3, IndianRupee } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { DASHBOARD_SIGNIN_URL } from '@/config/links';
import ProductFAQAccordion from '@/components/products/ProductFAQAccordion';

const cargoFaqs = [
  { question: 'Is cargo shipping suitable for heavy shipments?', answer: 'Yes, it is built for high-weight and oversized movement with specialized handling workflows.' },
  { question: 'How does cargo mode optimize cost?', answer: 'Route and load planning helps reduce per-shipment freight cost and improves lane efficiency.' },
  { question: 'Do I get dedicated operational support?', answer: 'Yes, cargo shipping includes dedicated support for planning and issue resolution.' },
  { question: 'Can enterprises use this for large distribution networks?', answer: 'Yes, the workflow is optimized for enterprise-scale freight operations.' },
  { question: 'How is tracking different from standard shipping?', answer: 'Cargo tracking includes milestone and exception visibility tuned for freight movement.' }
];

const ProductCargoShipping = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Cargo Shipping for Bulk and Heavy Shipments"
        description="Manage bulk and heavy cargo with cost-efficient planning, dedicated support, and enterprise-grade freight visibility."
        keywords="cargo shipping platform, heavy shipment logistics, bulk freight management, cargo cost optimization"
        canonical="https://boxdlogistics.in/products/cargo-shipping"
      />
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-b from-blue-100/50 via-blue-50/60 to-white px-4 pb-20 pt-28 md:pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59, 130, 246,0.22),transparent_40%)]" />
        <div className="mx-auto max-w-7xl">
          <div className="rounded-3xl border border-blue-100 bg-white p-8 shadow-xl md:p-12">
            <span className="inline-flex rounded-full bg-blue-100 px-4 py-1 text-sm font-semibold text-blue-900">Boxd Cargo · For B2B</span>
            <h1 className="mt-5 max-w-4xl text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
              Heavy-duty Cargo Shipping Built for Scale, Weight, and Efficiency
            </h1>
            <p className="mt-4 max-w-3xl text-lg text-gray-600">
              Move bulk consignments with better planning control, transparent freight costs, and dedicated logistics support from dispatch to POD.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14">
        <div className="mx-auto grid max-w-7xl gap-5 lg:grid-cols-3">
          {[
            { title: 'High Weight Handling', subtitle: 'Supports large dimensions and heavy loads', icon: Weight },
            { title: 'Dedicated Cargo Ops', subtitle: 'Priority support for bulk shipment operations', icon: ShieldCheck },
            { title: 'Freight Cost Intelligence', subtitle: 'Rate and lane-based optimization insights', icon: IndianRupee }
          ].map((item, index) => {
            const Icon = item.icon;
            const isAccent = index === 1;
            return (
              <div key={item.title} className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <span className={`inline-flex rounded-xl p-2 ${isAccent ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-900'}`}><Icon className="h-5 w-5" /></span>
                <h2 className="mt-4 text-2xl font-semibold text-gray-900">{item.title}</h2>
                <p className="mt-2 text-gray-600">{item.subtitle}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">Cargo vs Standard Shipping</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-blue-50 text-gray-700">
                <tr>
                  <th className="px-5 py-4 font-semibold">Capability</th>
                  <th className="px-5 py-4 font-semibold">Cargo Shipping</th>
                  <th className="px-5 py-4 font-semibold">Standard Shipping</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                {[
                  ['Shipment Weight', 'High-weight and oversized cargo support', 'Best for lightweight parcels'],
                  ['Planning Model', 'Lane and load optimization', 'Basic dispatch workflow'],
                  ['Support Coverage', 'Dedicated cargo operations team', 'General support queue'],
                  ['Cost Efficiency', 'Bulk shipment pricing advantages', 'Higher per-shipment variance'],
                  ['Transit Visibility', 'Freight milestone and exception tracking', 'Standard event tracking']
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-gray-200">
                    <td className="px-5 py-4 font-semibold text-gray-900">{row[0]}</td>
                    <td className="px-5 py-4">{row[1]}</td>
                    <td className="px-5 py-4">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-3xl font-bold text-gray-900">Efficiency Blocks for Bulk Operations</h2>
            <div className="mt-7 space-y-4">
              {[
                'Rate intelligence for lane-based cost optimization',
                'Reduced handling delays with standardized dispatch workflows',
                'Predictable freight movement for enterprise SLAs'
              ].map((point) => (
                <div key={point} className="flex items-start gap-3 rounded-xl bg-gray-50 p-4 text-gray-700">
                  <Boxes className="mt-0.5 h-4 w-4 text-blue-900" />
                  <p className="text-sm">{point}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-8">
            <h3 className="text-2xl font-semibold text-gray-900">Pricing & Performance Snapshot</h3>
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-blue-100 bg-white p-4">
                <p className="text-xs uppercase text-blue-900">Average cost reduction</p>
                <p className="mt-1 text-4xl font-bold text-gray-900">22%</p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-white p-4">
                <p className="text-xs uppercase text-blue-900">Improved dispatch efficiency</p>
                <p className="mt-1 text-4xl font-bold text-gray-900">34%</p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-white p-4">
                <p className="text-xs uppercase text-blue-900">Shipment visibility uptime</p>
                <p className="mt-1 text-4xl font-bold text-gray-900">99.2%</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-r from-blue-800 to-blue-900 px-8 py-12 text-center text-white">
          <BarChart3 className="mx-auto h-8 w-8 text-blue-100" />
          <h2 className="mt-4 text-3xl font-bold md:text-4xl">Scale Cargo Operations with Confidence</h2>
          <p className="mt-3 text-blue-100">Control freight costs, reduce delays, and move heavy shipments with a purpose-built cargo stack.</p>
          <Button className="mt-7 bg-white text-blue-900 hover:bg-blue-50" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
            Start Shipping Smarter Today <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <ProductFAQAccordion title="Cargo Shipping FAQs" items={cargoFaqs} />

      <Footer />
    </div>
  );
};

export default ProductCargoShipping;
